import trackLead, { LeadData } from 'core/services/trackLead'

const REQUEST_TIMEOUT = 5000 // ms

/**
 * Submits lead data to a server.
 *
 * This function takes a `valuesForm` object as a parameter, which contains
 * information about the lead. It then makes an asynchronous request to a server
 * to submit this data.
 *
 * @param valuesForm - An object containing information about the lead. This
 * object should have properties for the lead's information. It can also optionally include
 * details about the lead's spouse and children.
 *
 * @returns A Promise that resolves when the data has been successfully
 * submitted to the server, or rejects if there was an error submitting the data.
 */
async function submitLead(valuesForm: { [key: string]: string }): Promise<void> {
  const convertGender = (gender?: string): 'MALE' | 'FEMALE' => {
    return gender === 'm' || gender === 'M' ? 'MALE' : 'FEMALE'
  }

  // Extract answers data from valuesForm
  const answers: LeadData = {
    firstName: valuesForm.fname,
    gender: convertGender(valuesForm.gender),
    dateOfBirth: valuesForm.dob,
    emailAddress: valuesForm.email,
    phoneNumber: valuesForm.phone,
    includeSpouse: valuesForm.dob_s && valuesForm.gender_s ? true : false,
    spouseDetails: {},
    includeChild: false,
    children: [],
    jornayaLeadId: valuesForm.leadIdToken,
    // The type of valuesForm is supposedly an object of string/string pairs, but zip is an object
    zip: valuesForm.zip?.location?.zipCode,
    state: valuesForm.zip?.location?.stateAcronym,
    county: valuesForm.zip?.location?.countyName
  }

  // Extract spouse data from valuesForm
  if (valuesForm.dob_s && valuesForm.gender_s) {
    answers.spouseDetails = {
      gender: convertGender(valuesForm.gender_s),
      dateOfBirth: valuesForm.dob_s
    }
  }

  // Extract children data from valuesForm
  for (let i = 1; i <= 10; i++) {
    const genderKey = `gender_${i}`
    const dobKey = `dob_${i}`

    if (!valuesForm[genderKey] && !valuesForm[dobKey]) {
      break
    }

    answers.children.push({
      gender: convertGender(valuesForm[genderKey]),
      dateOfBirth: valuesForm[dobKey]
    })
    answers.includeChild = true
  }

  return await trackLead(answers, REQUEST_TIMEOUT)
}

export default submitLead
